/**
 * Configuration for the flows highlighted on the Landing page.
 * Before/after assets come from the same R2 CDN paths already used by each
 * flow's in-product hero — keeps a single source of truth.
 *
 * Top row = image→image flows. Bottom row = image→video flows (the
 * showcase tile auto-detects `.mp4`/`.webm`/`.mov` and renders a looping
 * muted `<video>` in place of `<img>`).
 */

const CDN_BASE = "https://cdn.todowith.ai/generated/2ae2c1db-53d0-4920-9cf4-a6b5a96db17b";

/** DOM id for the landing flows showcase — target of the hero CTA scroll. */
export const FLOWS_ANCHOR_ID = "flows";

export type FlowKey =
  | "profilePhoto"
  | "productStudio"
  | "productCreative"
  | "baseballTrend"
  | "talkingAvatar";

export interface LandingFlow {
  key: FlowKey;
  /** Where the "Try now" CTA navigates to. */
  route: string;
  /** i18n key under `t.landingFlows.flows.{key}`. Title + description live there. */
  nameKey: FlowKey;
  beforeUrl: string;
  afterUrl: string;
}

export const LANDING_IMAGE_FLOWS: LandingFlow[] = [
  {
    key: "profilePhoto",
    route: "/profile-photo",
    nameKey: "profilePhoto",
    beforeUrl: `${CDN_BASE}/hero-profile-photo-before.webp`,
    afterUrl: `${CDN_BASE}/hero-profile-photo-after.webp`,
  },
  {
    key: "productStudio",
    route: "/generate-product-catalog",
    nameKey: "productStudio",
    beforeUrl: `${CDN_BASE}/hero-studio-before.webp`,
    afterUrl: `${CDN_BASE}/hero-studio-after-1.webp`,
  },
  {
    key: "productCreative",
    route: "/product-creative",
    nameKey: "productCreative",
    beforeUrl: `${CDN_BASE}/hero-product-creative-before.webp`,
    afterUrl: `${CDN_BASE}/hero-product-creative-after.webp`,
  },
];

export const LANDING_VIDEO_FLOWS: LandingFlow[] = [
  {
    key: "baseballTrend",
    route: "/baseball-trend",
    nameKey: "baseballTrend",
    beforeUrl: `${CDN_BASE}/hero-profile-photo-before.webp`,
    afterUrl: `${CDN_BASE}/hero-baseball-trend-after-v2.mp4`,
  },
  {
    key: "talkingAvatar",
    route: "/talking-avatar",
    nameKey: "talkingAvatar",
    beforeUrl: "https://cdn.todowith.ai/hero/hero-talking-avatar-before.webp",
    // Audio-stripped variant — this decorative tile has no unmute button and
    // must never emit sound. The /talking-avatar page keeps the audio version
    // (TALKING_AVATAR_HERO_AFTER) for its speaker button.
    afterUrl: "https://cdn.todowith.ai/hero/hero-talking-avatar-after-v2-muted.mp4",
  },
];
